import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  EventqBooleanToggle,
  type EventqBooleanToggleVariant,
} from '../../primitives/boolean-toggle/boolean-toggle';
import type {
  EventqToggleListItem,
  EventqToggleListItemChange,
  EventqToggleListLabelPosition,
} from './toggle-list.types';

export type {
  EventqToggleListItem,
  EventqToggleListItemChange,
  EventqToggleListLabelPosition,
  EventqToggleListVariant,
} from './toggle-list.types';

const DEFAULT_SKELETON_COUNT = 3;

@Component({
  selector: 'eventq-toggle-list',
  imports: [EventqBooleanToggle],
  templateUrl: './toggle-list.html',
  styleUrl: './toggle-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'eventq-toggle-list-host',
    '[attr.aria-busy]': "loading() ? 'true' : 'false'",
  },
})
export class EventqToggleList {
  readonly items = input<readonly EventqToggleListItem[]>([]);
  /**
   * Default label placement for rows. Per-item `labelPosition` overrides this.
   * `'end'` = switch then label; `'start'` = label then switch.
   */
  readonly labelPosition = input<EventqToggleListLabelPosition>('end');
  readonly variant = input<EventqBooleanToggleVariant>('amber-dark');
  /**
   * When true, replaces rows with skeleton placeholders. Toggles are not rendered.
   */
  readonly loading = input(false);
  /** Explicit skeleton row count. When unset, uses `items.length` or 3. */
  readonly skeletonCount = input<number | null>(null);

  readonly itemChange = output<EventqToggleListItemChange>();

  readonly skeletonIndexes = computed(() => {
    const explicit = this.skeletonCount();
    const count =
      explicit != null && explicit > 0
        ? explicit
        : this.items().length > 0
          ? this.items().length
          : DEFAULT_SKELETON_COUNT;
    return Array.from({ length: count }, (_, index) => index);
  });

  resolvedLabelPosition(item: EventqToggleListItem): EventqToggleListLabelPosition {
    return item.labelPosition ?? this.labelPosition();
  }

  onToggle(item: EventqToggleListItem, checked: boolean): void {
    if (item.disabled) {
      return;
    }
    this.itemChange.emit({ id: item.id, checked });
  }
}
